/**
 * The pure view model deciding which of this screen's five states renders.
 * A failed profile read and a `profile: null` collapse to the identical
 * `"unavailable"` phase on purpose: a fan must not be able to tell a block
 * apart from a missing account or a typo.
 */
import type { typeAwardChip } from "../awards/awardChips";
import { type typeAwardCounts, ZERO_AWARD_COUNTS } from "../awards/awardCounts";
import type { typeSharedPoolRow } from "./SharedPoolsList";

export type typeFanProfilePhase =
  "noAccountId" | "loading" | "unavailable" | "private" | "full";

export type typeFanProfileView = {
  phase: typeFanProfilePhase;
  displayName: string;
  username: string;
  photoUrl: string | null;
  favoriteTeamIds: readonly string[];
  thisSeason: typeAwardCounts;
  allTime: typeAwardCounts;
  chips: readonly typeAwardChip[];
  pools: readonly typeSharedPoolRow[];
  hasAwardsFailed: boolean;
  hasPoolsFailed: boolean;
  arePoolsLoading: boolean;
  areAwardsLoading: boolean;
};

export function buildFanProfileView(input: {
  accountId: string | undefined;
  isProfileLoading: boolean;
  profile:
    | {
        accountId: string;
        username: string;
        displayName: string;
        photoUrl?: string | null;
        isDiscoverable: boolean;
        favoriteTeamIds: string[];
      }
    | null
    | undefined;
  hasProfileFailed: boolean;
  awards:
    | {
        thisSeason: typeAwardCounts;
        allTime: typeAwardCounts;
        awards: readonly typeAwardChip[];
      }
    | null
    | undefined;
  areAwardsLoading: boolean;
  hasAwardsFailed: boolean;
  pools: readonly typeSharedPoolRow[] | undefined;
  arePoolsLoading: boolean;
  hasPoolsFailed: boolean;
}): typeFanProfileView {
  const pools = input.pools ?? [];
  const pass = {
    hasAwardsFailed: input.hasAwardsFailed,
    hasPoolsFailed: input.hasPoolsFailed,
    arePoolsLoading: input.arePoolsLoading,
    areAwardsLoading: input.areAwardsLoading,
  };

  if (input.accountId === undefined || input.accountId === "") {
    return {
      phase: "noAccountId",
      displayName: "",
      username: "",
      photoUrl: null,
      favoriteTeamIds: [],
      thisSeason: ZERO_AWARD_COUNTS,
      allTime: ZERO_AWARD_COUNTS,
      chips: [],
      pools: [],
      ...pass,
    };
  }

  if (input.isProfileLoading) {
    return {
      phase: "loading",
      displayName: "",
      username: "",
      photoUrl: null,
      favoriteTeamIds: [],
      thisSeason: ZERO_AWARD_COUNTS,
      allTime: ZERO_AWARD_COUNTS,
      chips: [],
      pools: [],
      ...pass,
    };
  }

  if (
    input.hasProfileFailed ||
    input.profile === null ||
    input.profile === undefined
  ) {
    return {
      phase: "unavailable",
      displayName: "",
      username: "",
      photoUrl: null,
      favoriteTeamIds: [],
      thisSeason: ZERO_AWARD_COUNTS,
      allTime: ZERO_AWARD_COUNTS,
      chips: [],
      pools: [],
      ...pass,
    };
  }

  const photoUrl = input.profile.photoUrl ?? null;

  if (!input.profile.isDiscoverable) {
    return {
      phase: "private",
      displayName: input.profile.displayName,
      username: input.profile.username,
      photoUrl,
      favoriteTeamIds: [],
      thisSeason: ZERO_AWARD_COUNTS,
      allTime: ZERO_AWARD_COUNTS,
      chips: [],
      pools,
      ...pass,
    };
  }

  return {
    phase: "full",
    displayName: input.profile.displayName,
    username: input.profile.username,
    photoUrl,
    favoriteTeamIds: input.profile.favoriteTeamIds,
    thisSeason: input.awards?.thisSeason ?? ZERO_AWARD_COUNTS,
    allTime: input.awards?.allTime ?? ZERO_AWARD_COUNTS,
    chips: input.awards?.awards ?? [],
    pools,
    ...pass,
  };
}
